import { useEffect, useMemo, useState } from "react";

import {
  Row,
  Col,
  Form,
  Button,
  Modal,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";

import { numberFormatter } from "../../lib/utils";
import useAuthStore from "../../hooks/useAuthStore";
import { API } from "../../constants";

const ITEMS_PER_PAGE = 5;

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);

  const { user } = useAuthStore();

  const loggedInUser = user?.user || user || null;

  const customerId = loggedInUser?._id || loggedInUser?.id || null;

  const fetchOnlineOrders = async () => {
    if (!customerId) {
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const apiResponse = await fetch(
        `${API.FETCH_ONLINE_ORDER}/${customerId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );

      const result = await apiResponse.json();

      if (!apiResponse.ok || result?.status === false) {
        throw new Error(
          result?.message || "Unable to fetch online orders",
        );
      }

      setOrders(Array.isArray(result?.data) ? result.data : []);
    } catch (fetchError) {
      console.error("Fetch online orders error:", fetchError);

      setOrders([]);

      setError(
        fetchError?.message ||
          "Something went wrong while fetching your orders.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId) {
      return;
    }

    fetchOnlineOrders();
  }, [customerId]);

  const formatMoney = (value) => {
    return numberFormatter.format(Number(value || 0));
  };

  const formatDate = (value) => {
    if (!value) {
      return "Not available";
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
      const [year, month, day] = String(value)
        .split("-")
        .map(Number);

      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(new Date(year, month - 1, day));
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const getOrderNumber = (order) => {
    if (order.orderNumber) {
      return order.orderNumber;
    }

    if (order.ticketNo) {
      return order.ticketNo;
    }

    if (order.orderId) {
      return `#${String(order.orderId).slice(-8).toUpperCase()}`;
    }

    return "N/A";
  };

  const getPurchaseTypeLabel = (purchaseType) => {
    switch (purchaseType) {
      case "ticket_food":
        return "Tickets + Food";

      case "ticket":
        return "Movie Tickets";

      case "food":
        return "Food Order";

      default:
        return "Online Order";
    }
  };

  const getPurchaseTypeIcon = (purchaseType) => {
    switch (purchaseType) {
      case "ticket_food":
        return "fa-solid fa-ticket";

      case "ticket":
        return "fa-solid fa-film";

      case "food":
        return "fa-solid fa-burger";

      default:
        return "fa-solid fa-bag-shopping";
    }
  };

  const getStatusIcon = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "confirmed":
        return "fa-solid fa-circle-check";

      case "completed":
        return "fa-solid fa-check-double";

      case "cancelled":
        return "fa-solid fa-circle-xmark";

      case "pending":
        return "fa-solid fa-clock";

      default:
        return "fa-solid fa-circle-info";
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };


 
 
  const handleCloseDetails = () => {
    setShowOrderDetails(false);

    setTimeout(() => {
      setSelectedOrder(null);
    }, 200);
  };

  const handleRefresh = () => {
    if (!customerId || loading) {
      return;
    }

    fetchOnlineOrders();
  };



  const filteredOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return orders.filter((order) => {
      const statusMatches =
        statusFilter === "all" ||
        String(order.status || "").toLowerCase() ===
          statusFilter.toLowerCase();

      const typeMatches =
        typeFilter === "all" || order.purchaseType === typeFilter;

      if (!statusMatches || !typeMatches) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const foodNames = (order.foodItems || [])
        .map((item) => item.name || "")
        .join(" ");

      const ticketNames = (order.ticketItems || [])
        .map((item) => item.name || "")
        .join(" ");

      const searchableText = [
        order.orderNumber,
        order.ticketNo,
        order.orderId,
        order.movie,
        order.theatre,
        order.screen,
        order.customerName,
        order.customerEmail,
        foodNames,
        ticketNames,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [orders, search, statusFilter, typeFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, typeFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / ITEMS_PER_PAGE),
  );

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    setCurrentPage(page);
  };

  if (loading && customerId && orders.length === 0) {
    return (
      <div className="dashboard-edit-profile-from">
        <div className="online-orders-wrapper">
          <div className="orders-loading">
            <Spinner animation="border" variant="danger" />

            <h5>Loading your orders...</h5>

            <p>Please wait while we fetch your online orders.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-edit-profile-from">
      <div className="online-orders-wrapper">
        <div className="orders-page-header">
          <div>
            <span className="orders-page-eyebrow">
              ORDER HISTORY
            </span>

            <h3>My Online Orders</h3>

            <p>
              View your movie tickets, food orders, and order details.
            </p>
          </div>

          <Button
            type="button"
            className="orders-refresh-btn"
            onClick={handleRefresh}
            disabled={loading || !customerId}
          >
            {loading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />

                Refreshing
              </>
            ) : (
              <>
                <i className="fa-solid fa-rotate-right me-2"></i>
                Refresh
              </>
            )}
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="orders-error-alert">
            <i className="fa-solid fa-circle-exclamation me-2"></i>

            {error}
          </Alert>
        )}

        <Row className="g-3 orders-stats-row">
          <Col lg={3} md={6}>
            <div className="order-stat-card">
              <div className="order-stat-icon">
                <i className="fa-solid fa-receipt"></i>
              </div>

              <div>
                <span>Total Orders</span>

                <strong>{orders.length}</strong>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6}>
            <div className="order-stat-card">
              <div className="order-stat-icon">
                <i className="fa-solid fa-ticket"></i>
              </div>

              <div>
                <span>Ticket Orders</span>

                <strong>
                  {orders.filter((order) => order.hasTickets).length}
                </strong>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6}>
            <div className="order-stat-card">
              <div className="order-stat-icon">
                <i className="fa-solid fa-burger"></i>
              </div>

              <div>
                <span>Food Orders</span>

                <strong>
                  {orders.filter((order) => order.hasFood).length}
                </strong>
              </div>
            </div>
          </Col>

          <Col lg={3} md={6}>
            <div className="order-stat-card">
              <div className="order-stat-icon">
                <i className="fa-solid fa-circle-check"></i>
              </div>

              <div>
                <span>Confirmed</span>

                <strong>
                  {
                    orders.filter(
                      (order) => order.status === "Confirmed",
                    ).length
                  }
                </strong>
              </div>
            </div>
          </Col>
        </Row>

        <div className="orders-filter-box">
          <Row className="g-3 align-items-end">
            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label>Order Type</Form.Label>

                <Form.Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Orders</option>

                  <option value="ticket">Movie Tickets</option>

                  <option value="food">Food Only</option>

                  <option value="ticket_food">Tickets + Food</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>

                <Form.Select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value)
                  }
                >
                  <option value="all">All Statuses</option>

                  <option value="confirmed">Confirmed</option>

                  <option value="completed">Completed</option>

                  <option value="pending">Pending</option>

                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={6} md={12}>
              <Form.Group>
                <Form.Label>Search Orders</Form.Label>

                <div className="orders-search-wrapper">
                  <i className="fa-solid fa-magnifying-glass"></i>

                  <Form.Control
                    value={search}
                    onChange={(e) =>
                      setSearch(e.target.value)
                    }
                    placeholder="Search movie, order number, ticket number, food..."
                  />

                  {search && (
                    <button
                      type="button"
                      className="orders-search-clear"
                      onClick={() => setSearch("")}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {!loading && orders.length > 0 && (
          <div className="orders-result-count">
            Showing{" "}
            <strong>{filteredOrders.length}</strong>{" "}
            {filteredOrders.length === 1
              ? "order"
              : "orders"}
          </div>
        )}

        {paginatedOrders.length > 0 ? (
          <div className="orders-list">
            {paginatedOrders.map((order) => (
              <div
                className="online-order-card"
                key={order.orderId}
              >
                <div className="online-order-card-header">
                  <div className="order-number-section">
                    <div className="order-type-icon">
                      <i
                        className={getPurchaseTypeIcon(
                          order.purchaseType,
                        )}
                      ></i>
                    </div>

                    <div>
                      <span>Order</span>

                      <h5>{getOrderNumber(order)}</h5>

                      <div className="order-type-text">
                        {getPurchaseTypeLabel(
                          order.purchaseType,
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`online-order-status status-${String(
                      order.status || "pending",
                    ).toLowerCase()}`}
                  >
                    <i
                      className={getStatusIcon(order.status)}
                    ></i>

                    {order.status || "Pending"}
                  </div>
                </div>

                <div className="online-order-card-body">
                  <Row className="g-4">
                    {order.hasTickets && (
                      <Col lg={order.hasFood ? 6 : 8}>
                        <div className="order-content-section">
                          <div className="order-section-heading">
                            <i className="fa-solid fa-film"></i>
                            Movie Details
                          </div>

                          <h4 className="order-movie-title">
                            {order.movie || "Movie"}
                          </h4>

                          <div className="order-info-grid">
                            <div className="order-info-item">
                              <span>Theatre</span>

                              <strong>
                                {order.theatre ||
                                  "Lindsay Drive-In"}
                              </strong>
                            </div>

                            <div className="order-info-item">
                              <span>Screen</span>

                              <strong>
                                {order.screen ||
                                  "Not available"}
                              </strong>
                            </div>

                            <div className="order-info-item">
                              <span>Show Date</span>

                              <strong>
                                {formatDate(order.date)}
                              </strong>
                            </div>

                            <div className="order-info-item">
                              <span>Show Time</span>

                              <strong>
                                {order.showtime ||
                                  "Not available"}
                              </strong>
                            </div>

                            <div className="order-info-item">
                              <span>Tickets</span>

                              <strong>
                                {order.tickets || 0}
                              </strong>
                            </div>

                            <div className="order-info-item">
                              <span>Ticket Total</span>

                              <strong>
                                {formatMoney(
                                  order.ticketTotal,
                                )}
                              </strong>
                            </div>

                            {order.ticketNo && (
                              <div className="order-info-item">
                                <span>Ticket No.</span>

                                <strong>
                                  {order.ticketNo}
                                </strong>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}

                    {order.hasFood && (
                      <Col
                        lg={order.hasTickets ? 6 : 8}
                      >
                        <div className="order-content-section">
                          <div className="order-section-heading">
                            <i className="fa-solid fa-burger"></i>
                            Food
                          </div>

                          <div className="food-preview-list">
                            {(order.foodItems || [])
                              .slice(0, 3)
                              .map((food, index) => (
                                <div
                                  className="food-preview-item"
                                  key={
                                    food.itemId || index
                                  }
                                >
                                  <div>
                                    <strong>
                                      {food.name ||
                                        "Food Item"}
                                    </strong>

                                    <span>
                                      Qty:{" "}
                                      {food.quantity}
                                    </span>
                                  </div>

                                  <strong>
                                    {formatMoney(
                                      food.total,
                                    )}
                                  </strong>
                                </div>
                              ))}

                            {(order.foodItems || [])
                              .length > 3 && (
                              <div className="food-more-items">
                                +
                                {order.foodItems.length -
                                  3}{" "}
                                more{" "}
                                {order.foodItems.length -
                                  3 ===
                                1
                                  ? "item"
                                  : "items"}
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col
                      lg={
                        order.hasTickets &&
                        order.hasFood
                          ? 12
                          : 4
                      }
                    >
                      <div className="order-payment-summary">
                        <div>
                          <span>Total Amount</span>

                          <strong className="order-total-amount">
                            {formatMoney(
                              order.totalAmount ??
                                order.price,
                            )}
                          </strong>
                        </div>

                        <div className="payment-details-mini">
                          <span>
                            <i className="fa-solid fa-credit-card"></i>

                            {order.paymentMethod ||
                              "Online"}
                          </span>

                          <span
                            className={`payment-status payment-status-${String(
                              order.paymentStatus ||
                                "pending",
                            ).toLowerCase()}`}
                          >
                            {order.paymentStatus ||
                              "Pending"}
                          </span>
                        </div>

                        <Button
                          type="button"
                          className="view-order-btn"
                          onClick={() =>
                            handleViewDetails(order)
                          }
                        >
                          <i className="fa-solid fa-eye me-2"></i>
                          View Details
                        </Button>

                  
                          <Button
                            type="button"
                            className="download-ticket-btn"
                            
                          >
                            <i className="fa-solid fa-download me-2"></i>
                            Download Invoice
                          </Button>
                        
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !loading && (
            <div className="orders-empty-state">
              <div className="orders-empty-icon">
                <i className="fa-solid fa-receipt"></i>
              </div>

              <h4>
                {orders.length === 0
                  ? "No online orders yet"
                  : "No matching orders"}
              </h4>

              <p>
                {orders.length === 0
                  ? "Your movie ticket and food orders will appear here once you place an order."
                  : "Try changing your search or filter options."}
              </p>

              {orders.length > 0 && (
                <Button
                  variant="outline-danger"
                  className="mt-3"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )
        )}

        {filteredOrders.length >
          ITEMS_PER_PAGE && (
          <div className="orders-pagination-wrapper">
            <button
              type="button"
              className="order-pagination-btn"
              disabled={currentPage === 1}
              onClick={() =>
                goToPage(currentPage - 1)
              }
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            {Array.from(
              {
                length: totalPages,
              },
              (_, index) => index + 1,
            ).map((page) => (
              <button
                type="button"
                key={page}
                className={`order-pagination-btn ${
                  currentPage === page
                    ? "active"
                    : ""
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              className="order-pagination-btn"
              disabled={
                currentPage === totalPages
              }
              onClick={() =>
                goToPage(currentPage + 1)
              }
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      {/* VIEW DETAILS MODAL */}
      <Modal
        show={showOrderDetails}
        onHide={handleCloseDetails}
        size="lg"
        centered
        className="online-order-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="modal-order-title">
              <span>Order Details</span>

              {selectedOrder && (
                <small>
                  {getOrderNumber(selectedOrder)}
                </small>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedOrder && (
            <>
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <div className="modal-summary-card">
                    <span>Status</span>

                    <div
                      className={`online-order-status status-${String(
                        selectedOrder.status ||
                          "pending",
                      ).toLowerCase()}`}
                    >
                      <i
                        className={getStatusIcon(
                          selectedOrder.status,
                        )}
                      ></i>

                      {selectedOrder.status ||
                        "Pending"}
                    </div>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="modal-summary-card">
                    <span>Order Type</span>

                    <strong>
                      {getPurchaseTypeLabel(
                        selectedOrder.purchaseType,
                      )}
                    </strong>
                  </div>
                </Col>

                <Col md={4}>
                  <div className="modal-summary-card">
                    <span>Total</span>

                    <strong className="modal-total-price">
                      {formatMoney(
                        selectedOrder.totalAmount ??
                          selectedOrder.price,
                      )}
                    </strong>
                  </div>
                </Col>
              </Row>

              <div className="order-modal-section">
                <h5>
                  <i className="fa-solid fa-circle-info"></i>
                  Order Information
                </h5>

                <Row className="g-3">
                  <Col md={6}>
                    <div className="modal-info-item">
                      <span>Order Number</span>

                      <strong>
                        {getOrderNumber(
                          selectedOrder,
                        )}
                      </strong>
                    </div>
                  </Col>

                  {selectedOrder.ticketNo && (
                    <Col md={6}>
                      <div className="modal-info-item">
                        <span>
                          Ticket Number
                        </span>

                        <strong>
                          {
                            selectedOrder.ticketNo
                          }
                        </strong>
                      </div>
                    </Col>
                  )}

                  <Col md={6}>
                    <div className="modal-info-item">
                      <span>
                        Payment Method
                      </span>

                      <strong className="text-capitalize">
                        {selectedOrder.paymentMethod ||
                          "Online"}
                      </strong>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="modal-info-item">
                      <span>
                        Payment Status
                      </span>

                      <strong className="text-capitalize">
                        {selectedOrder.paymentStatus ||
                          "Pending"}
                      </strong>
                    </div>
                  </Col>

                  {selectedOrder.customerName && (
                    <Col md={6}>
                      <div className="modal-info-item">
                        <span>Customer</span>

                        <strong>
                          {
                            selectedOrder.customerName
                          }
                        </strong>
                      </div>
                    </Col>
                  )}

                  {selectedOrder.customerEmail && (
                    <Col md={6}>
                      <div className="modal-info-item">
                        <span>Email</span>

                        <strong>
                          {
                            selectedOrder.customerEmail
                          }
                        </strong>
                      </div>
                    </Col>
                  )}

                  {selectedOrder.customerPhone && (
                    <Col md={6}>
                      <div className="modal-info-item">
                        <span>Phone</span>

                        <strong>
                          {
                            selectedOrder.customerPhone
                          }
                        </strong>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>

              {selectedOrder.hasTickets && (
                <div className="order-modal-section">
                  <h5>
                    <i className="fa-solid fa-film"></i>
                    Movie Details
                  </h5>

                  <div className="modal-movie-card">
                    <div className="modal-movie-icon">
                      <i className="fa-solid fa-film"></i>
                    </div>

                    <div className="modal-movie-content">
                      <h4>
                        {selectedOrder.movie ||
                          "Movie"}
                      </h4>

                      <Row className="g-3">
                        <Col sm={6}>
                          <span>Theatre</span>

                          <strong>
                            {selectedOrder.theatre ||
                              "Lindsay Drive-In"}
                          </strong>
                        </Col>

                        <Col sm={6}>
                          <span>Screen</span>

                          <strong>
                            {selectedOrder.screen ||
                              "Not available"}
                          </strong>
                        </Col>

                        <Col sm={6}>
                          <span>Show Date</span>

                          <strong>
                            {formatDate(
                              selectedOrder.date,
                            )}
                          </strong>
                        </Col>

                        <Col sm={6}>
                          <span>Show Time</span>

                          <strong>
                            {selectedOrder.showtime ||
                              "Not available"}
                          </strong>
                        </Col>

                        <Col sm={6}>
                          <span>
                            Total Tickets
                          </span>

                          <strong>
                            {selectedOrder.tickets ||
                              0}
                          </strong>
                        </Col>

                        <Col sm={6}>
                          <span>
                            Ticket Total
                          </span>

                          <strong>
                            {formatMoney(
                              selectedOrder.ticketTotal,
                            )}
                          </strong>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              )}

              {selectedOrder.hasTickets && (
                <div className="order-modal-section">
                  <h5>
                    <i className="fa-solid fa-ticket"></i>
                    Ticket Price Breakdown
                  </h5>

                  <div className="order-table-wrapper">
                    <Table
                      responsive
                      className="order-items-table"
                    >
                      <thead>
                        <tr>
                          <th>Ticket</th>

                          <th>Unit Price</th>

                          <th>Qty</th>

                          <th>Tax</th>

                          <th className="text-end">
                            Total
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {(selectedOrder.ticketItems ||
                          []).length >
                        0 ? (
                          (
                            selectedOrder.ticketItems ||
                            []
                          ).map(
                            (
                              ticket,
                              index,
                            ) => (
                              <tr
                                key={`${
                                  ticket.itemId ||
                                  "ticket"
                                }-${index}`}
                              >
                                <td>
                                  <strong>
                                    {ticket.name ||
                                      selectedOrder.movie ||
                                      "Movie Ticket"}
                                  </strong>
                                </td>

                                <td>
                                  {formatMoney(
                                    ticket.unitPrice ??
                                      ticket.price,
                                  )}
                                </td>

                                <td>
                                  {ticket.quantity ||
                                    0}
                                </td>

                                <td>
                                  {ticket.tax ?? 0}
                                </td>

                                <td className="text-end">
                                  <strong>
                                    {formatMoney(
                                      ticket.total,
                                    )}
                                  </strong>
                                </td>
                              </tr>
                            ),
                          )
                        ) : (
                          <tr>
                            <td>
                              <strong>
                                {selectedOrder.movie ||
                                  "Movie Ticket"}
                              </strong>
                            </td>

                            <td>-</td>

                            <td>
                              {selectedOrder.tickets ||
                                0}
                            </td>

                            <td>0</td>

                            <td className="text-end">
                              <strong>
                                {formatMoney(
                                  selectedOrder.ticketTotal,
                                )}
                              </strong>
                            </td>
                          </tr>
                        )}

                        <tr className="food-table-total">
                          <td colSpan={4}>
                            <strong>
                              Ticket Total
                            </strong>
                          </td>

                          <td className="text-end">
                            <strong>
                              {formatMoney(
                                selectedOrder.ticketTotal,
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              {selectedOrder.hasFood && (
                <div className="order-modal-section">
                  <h5>
                    <i className="fa-solid fa-burger"></i>
                    Food
                  </h5>

                  <div className="order-table-wrapper">
                    <Table
                      responsive
                      className="order-items-table"
                    >
                      <thead>
                        <tr>
                          <th>Item</th>

                          <th>Unit Price</th>

                          <th>Qty</th>

                          <th>Tax</th>

                          <th className="text-end">
                            Total
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {(
                          selectedOrder.foodItems ||
                          []
                        ).map(
                          (
                            food,
                            index,
                          ) => (
                            <tr
                              key={`${
                                food.itemId ||
                                "food"
                              }-${index}`}
                            >
                              <td>
                                <strong>
                                  {food.name ||
                                    "Food Item"}
                                </strong>
                              </td>

                              <td>
                                {formatMoney(
                                  food.unitPrice,
                                )}
                              </td>

                              <td>
                                {food.quantity ||
                                  0}
                              </td>

                              <td>
                                {food.tax}
                              </td>

                              <td className="text-end">
                                <strong>
                                  {formatMoney(
                                    food.total,
                                  )}
                                </strong>
                              </td>
                            </tr>
                          ),
                        )}

                        <tr className="food-table-total">
                          <td colSpan={4}>
                            <strong>
                              Food Total
                            </strong>
                          </td>

                          <td className="text-end">
                            <strong>
                              {formatMoney(
                                selectedOrder.foodTotal,
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <div className="order-modal-section mb-0">
                <h5>
                  <i className="fa-solid fa-wallet"></i>
                  Payment Summary
                </h5>

                <div className="payment-breakdown">
                  {selectedOrder.hasTickets && (
                    <div>
                      <span>Ticket Total</span>

                      <strong>
                        {formatMoney(
                          selectedOrder.ticketTotal,
                        )}
                      </strong>
                    </div>
                  )}

                  {selectedOrder.hasFood && (
                    <div>
                      <span>Food Total</span>

                      <strong>
                        {formatMoney(
                          selectedOrder.foodTotal,
                        )}
                      </strong>
                    </div>
                  )}

                  {Number(
                    selectedOrder.discountAmount ||
                      0,
                  ) > 0 && (
                    <div>
                      <span>Discount</span>

                      <strong>
                        -
                        {formatMoney(
                          selectedOrder.discountAmount,
                        )}
                      </strong>
                    </div>
                  )}

                  <div className="payment-breakdown-total">
                    <span>Total Amount</span>

                    <strong>
                      {formatMoney(
                        selectedOrder.totalAmount ??
                          selectedOrder.price,
                      )}
                    </strong>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDetails}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

     
    </div>
  );
};

export default OrderList;